import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import {
  FORM_THEMES, FormHeader, EnhancedShutdownInfo, PhotoGrid,
  ErrorBanner, FormBottomBar, captureGPS as doGPS, processPhoto
} from "./FormShared";

const theme = FORM_THEMES.dtc;

export default function DTCForm() {
  const { dtId } = useParams();
  const navigate = useNavigate();
  const [dt, setDT] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [shutdowns, setShutdowns] = useState([]);
  const [hasActiveShutdown, setHasActiveShutdown] = useState(false);
  const [activeShutdownId, setActiveShutdownId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("pending");
  const [gpsData, setGpsData] = useState({ lat: "", lng: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([null, null, null]);

  const photoLabels = ["Before", "Cable", "After"];

  useEffect(() => { api.get("/auth/me").then(r => setCurrentUser(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dtRes = await api.get(`/dt-master/${dtId}`);
        setDT(dtRes.data);
        const shutRes = await api.get("/shutdowns/available", { params: { feeder_code: dtRes.data.feeder_code } });
        const items = shutRes.data.items || [];
        setShutdowns(items);
        // DTC requires an active "now" shutdown
        const activeNow = items.find(s => s.availability === "now");
        if (activeNow) {
          setHasActiveShutdown(true);
          setActiveShutdownId(activeNow.id);
        }
      } catch { navigate("/dt-master"); }
    };
    fetchData();
  }, [dtId]);

  const captureGPS = useCallback((silent = false) => doGPS(setGpsStatus, setGpsData, silent, toast), []);
  useEffect(() => { captureGPS(true); }, [captureGPS]);

  const handlePhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const metadata = {
      capturedAt: new Date().toISOString(),
      capturedDate: new Date().toLocaleDateString("en-IN"),
      capturedTime: new Date().toLocaleTimeString("en-IN"),
      gps_lat: gpsData.lat || "N/A", gps_lng: gpsData.lng || "N/A",
      technicianName: currentUser?.full_name || "Unknown",
      label: `DTC ${photoLabels[index]}`
    };
    const photoData = await processPhoto(file, metadata);
    const newPhotos = [...photos]; newPhotos[index] = photoData;
    setPhotos(newPhotos);
    toast.success(`${photoLabels[index]} photo captured`);
  };

  const removePhoto = (index) => {
    const p = [...photos]; p[index] = null; setPhotos(p);
  };

  const handleSubmit = async () => {
    if (!hasActiveShutdown) { toast.error("DTC can only be done during an active shutdown"); return; }
    if (!gpsData.lat) { toast.error("GPS location not captured. Please enable location services and tap 'Capture GPS'"); return; }
    if (!photos.some(p => p)) { toast.error("At least 1 photo is required"); return; }
    setLoading(true); setError(null);
    try {
      const photosData = photos.filter(p => p).map(p => ({
        url: `/mock/dtc/${Date.now()}_${p.filename}`,
        metadata: { capturedAt: p.capturedAt, gps_lat: p.gps_lat, gps_lng: p.gps_lng, technician: p.technicianName, label: p.label }
      }));
      await api.post("/installation/dtc", {
        dt_id: dtId, shutdown_id: activeShutdownId,
        gps_lat: parseFloat(gpsData.lat), gps_lng: parseFloat(gpsData.lng),
        photos: photosData.map(p => p.url), photos_metadata: photosData
      });
      toast.success("DTC completed!");
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "DTC submission failed";
      setError(msg); toast.error(msg, { duration: 10000 });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="dtc-form">
      <FormHeader theme={theme} dt={dt} gpsStatus={gpsStatus} gpsData={gpsData}
        onGpsClick={() => captureGPS()} onBack={() => navigate(-1)} />

      {/* Sub-header: technician + shutdown info */}
      <div className="px-3 pt-2 space-y-1.5">
        <div className="text-[10px] text-slate-400">Technician: {currentUser?.full_name}</div>
        <EnhancedShutdownInfo shutdowns={shutdowns} />
        {shutdowns.length > 0 && !hasActiveShutdown && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={13} className="text-red-500 shrink-0" />
            <span className="text-[11px] text-red-600 font-medium">DTC must be done during an active shutdown. Shutdown not active right now.</span>
          </div>
        )}
      </div>

      {/* Form Body */}
      <div className="flex-1 px-3 pt-3 pb-20 space-y-4">
        {/* Photos */}
        <PhotoGrid photos={photos} photoLabels={photoLabels} onUpload={handlePhotoUpload}
          onRemove={removePhoto} submitted={submitted} theme={theme} testIdPrefix="dtc" />

        {/* Safety note */}
        <div className="text-[10px] text-orange-600 font-medium px-1">
          Ensure feeder is de-energized before making connections
        </div>

        <ErrorBanner error={error} testId="dtc-error-banner" />
      </div>

      <FormBottomBar theme={theme} submitted={submitted} loading={loading}
        disabled={!hasActiveShutdown || !gpsData.lat} onSubmit={handleSubmit} dtId={dtId} navigate={navigate} />
    </div>
  );
}
